import { concatStringish } from '../../utils/strings/concatStringish';
import './FlatButton.css';

import { ButtonHTMLAttributes, FC, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

export enum Buttons {
  Outlined = 'outlined',
  Fab = 'Fab',
}

export type FlatButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  disabled?: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => Promise<unknown> | void;
  variant?: Buttons;
  colorVariant?: '' | 'red' | 'error' | 'green-5';
  sizeVariant?: 'small' | 'medium' | 'large';
  className?: string;
};

export const FlatButton: FC<FlatButtonProps> = ({
  children,
  className,
  disabled,
  onClick,
  variant,
  colorVariant,
  sizeVariant = 'medium',
  ...props
}) => {
  const [_, setDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const manuallyDisabled = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const timeoutRef = useRef<number | undefined>();

  const colorVariantClass = (() => {
    switch (colorVariant) {
      case 'error':
        return 'FlatButton--error';
      case 'green-5':
        return 'FlatButton--green-5';
      case 'red':
        return 'FlatButton--red';
      default:
        return '';
    }
  })();

  const sizeVariantClass = sizeVariant ? `FlatButton--${sizeVariant}` : '';
  const fabSizeVariantClass = variant === Buttons.Fab && sizeVariant === 'small' ? 'Fab--small' : '';

  const tailwindColor =
    variant === Buttons.Outlined
      ? colorVariant === 'error'
        ? '!bg-transparent !border-2 !border-[--error] !text-[--error]'
        : `!bg-transparent !border-2 !border-[--green-5] !text-[--green-5]`
      : '';

  const newClassName = concatStringish(
    'FlatButton',
    variant,
    colorVariantClass,
    sizeVariantClass,
    fabSizeVariantClass,
    tailwindColor,
    variant === Buttons.Outlined && sizeVariant !== 'small' ? '!px-3' : '',
    className,
  );

  const disabledOverride = manuallyDisabled.current || isLoading || disabled;

  /**
   * It is possible to click the button faster than react will re-render state
   * To prevent double-click submissions, we manually set the button's disabled attribute
   *
   * However, not all clicks are guaranteed to set it (and thus un-set it)
   * For exmaple, clicking submit on a form that has validation errors will never
   * put it into a loading or disabled state, so we add a timer to un-disable the button
   *
   * Finally, we add a useEffect hook that will clear out that timer if a parent component
   * DOES update the loading or disabled states- we shouldn't clear out a manually set disabled state
   * if an API call takes awhile, for example
   */

  const clearManuallySetDisabled = () => {
    manuallyDisabled.current = false;
    setDisabled(false);
    timeoutRef.current = undefined;
  };

  const startManualDisabled = useCallback(() => {
    if (timeoutRef.current) return;

    manuallyDisabled.current = true;
    setDisabled(true);

    timeoutRef.current = window.setTimeout(clearManuallySetDisabled, 750);
  }, []);

  const cleanupTimer = useCallback(() => {
    if (!timeoutRef.current) return;

    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  }, []);

  const clickHandler = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      if (manuallyDisabled.current) return;

      startManualDisabled();

      const result = onClick?.(e);

      if (result) {
        setIsLoading(true);
        await result;
        setIsLoading(false);
        clearManuallySetDisabled();
      }

      clearManuallySetDisabled();
    },
    [onClick, startManualDisabled],
  );

  useEffect(() => {
    return () => cleanupTimer();
  });

  return (
    <button
      {...props}
      ref={buttonRef}
      aria-disabled={disabledOverride}
      className={newClassName}
      onClick={disabled ? undefined : clickHandler}
    >
      {isLoading ? 'Loading' : children}
    </button>
  );
};
